import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../../../../../shared/icons/close-icon.svg';
import { OptionalMessageInputRef } from '../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import { ModalActionDescription, ModalActionTitle } from '../../../../../document-table/DocumentTableConstants';
import { Warning } from '../../../../../shared/warning';
import InviteReceivingTeamFormik from '../../../../../../forms/InviteReceivingTeamFormContainer';
import { DocumentLoadingSpinner } from '../../../../document-loading-spinner/DocumentLoadingSpinner';
import Tooltip from '../../../../../tooltip';
import { DocumentStatusEnum } from '../../../../../../shared/constants/document';
import { CounterpartyPermissionLevel, COUNTERPARTY_PERMISSION_LABELS } from '../../../../../../types/counterparty-permissions';
import styles from './SendSlideout.module.scss';

interface SendSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  isDealShared: boolean;
  handler: () => void;
  isLoading: boolean;
  suggestionsAlert: { title: string; text: string } | null;
  optionalMessageInputRef: React.RefObject<HTMLTextAreaElement>;
  teamId: string;
  showCounterparty?: boolean;
  counterpartyName?: string;
  counterpartyEmail?: string;
  hasCounterparty?: boolean;
  counterpartyPermissionLevel: CounterpartyPermissionLevel;
  onCounterpartyPermissionChange: (level: CounterpartyPermissionLevel) => void;
  documentSource?: string;
  documentStatus?: string;
  isUserDealCreator?: boolean;
  isDocumentCreator?: boolean;
}

const SendSlideout = ({
  isOpen,
  onClose,
  isDealShared,
  handler,
  isLoading,
  suggestionsAlert,
  optionalMessageInputRef,
  teamId,
  showCounterparty = false,
  counterpartyName,
  counterpartyEmail,
  hasCounterparty = false,
  counterpartyPermissionLevel,
  onCounterpartyPermissionChange,
  documentSource,
  documentStatus,
  isUserDealCreator,
  isDocumentCreator,
}: SendSlideoutProps) => {
  // Don't render anything if not showing
  if (!isOpen) {
    return null;
  }

  const validCounterparty = showCounterparty && counterpartyEmail && counterpartyName;
  const shouldShowInviteForm = !isDealShared && !hasCounterparty;
  const isExecuted = documentStatus === DocumentStatusEnum.EXECUTED;

  // Memoized container components for InviteReceivingTeamFormik
  const BodyContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__formSection}>{children}</div>
  ));

  const ActionsContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__footer}>{children}</div>
  ));

  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {isOpen && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div className={styles.slideout}>
          {/* Sticky Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              <FormattedMessage
                id="document-wizard-modal.send-title"
                defaultMessage={ModalActionTitle.SEND}
              />
            </div>
            <div className={styles.slideout__close} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
            {/* Show invitation form when no counterparty */}
            {shouldShowInviteForm ? (
              <InviteReceivingTeamFormik
                shouldAccessBeGranted={false}
                sendDocumentToReceiverHandler={handler}
                renderSecondaryAction={() => (
                  <Button variant="link" className="text-secondary" onClick={onClose}>
                    <FormattedMessage
                      id="lease-document-wizard-preview-send-offer.cta.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                )}
                bodyContainer={BodyContainer}
                actionsContainer={ActionsContainer}
                teamId={teamId}
                sendWithDocumentId={true}
                shareDocument={true}
                refreshCounterpartyData={() => {}}
              />
            ) : (
              /* Show normal send flow when counterparty exists */
              <div className={styles.slideout__form}>
                {/* Counterparty Info */}
                {validCounterparty && (
                  <div className={styles.slideout__counterparty}>
                    <strong>Counterparty:</strong>&nbsp;{counterpartyName} ({counterpartyEmail})
                  </div>
                )}

                {/* Suggestions Warning */}
                {suggestionsAlert && (
                  <div className={styles.slideout__warning}>
                    <Warning text={suggestionsAlert.text} title={suggestionsAlert.title} />
                  </div>
                )}

                {/* Description - only show if deal is shared */}
                {isDealShared && (
                  <div className={styles.slideout__description}>
                    <FormattedMessage
                      id="lease-document-wizard-preview-send-offer.description"
                      defaultMessage={ModalActionDescription.SEND}
                    />
                  </div>
                )}

                {/* Permission Dropdown - Only show for document creators sending imported PDFs to counterparty */}
                {!isExecuted && isDocumentCreator && documentSource === 'imported' && (
                  <div className={styles.slideout__permissionsSection}>
                    <div className={styles.slideout__permissionRow}>
                      <label className={styles.slideout__permissionLabel}>
                        <span>Counterparty Permissions</span>
                        <Tooltip
                          testId="counterparty-permission-tooltip"
                          placement="top"
                        >
                          Set counterparty capabilities
                        </Tooltip>
                      </label>
                      <select
                        className={styles.slideout__permissionSelect}
                        value={counterpartyPermissionLevel}
                        onChange={(e) => onCounterpartyPermissionChange(e.target.value as CounterpartyPermissionLevel)}
                      >
                        {Object.entries(COUNTERPARTY_PERMISSION_LABELS).map(([level, label]) => (
                          <option key={level} value={level}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Optional Message Input */}
                <div className={styles.slideout__messageSection}>
                  <OptionalMessageInputRef ref={optionalMessageInputRef} />
                </div>
              </div>
            )}
          </div>

          {/* Footer with Actions - only show when not showing invite form (invite form has its own footer) */}
          {!shouldShowInviteForm && (
            <div className={styles.slideout__footer}>
              <Button variant="link" className="text-secondary" onClick={onClose}>
                <FormattedMessage
                  id="lease-document-wizard-preview-send-offer.cta.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button variant="secondary" onClick={handler} disabled={isLoading}>
                <FormattedMessage
                  id="lease-document-wizard-preview-send-offer.cta.send-to-tenant"
                  defaultMessage="Send"
                />
                {isLoading && <DocumentLoadingSpinner />}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default SendSlideout;
